import { Component } from '@theme/component';

/**
 * A custom element that keeps the main product form's add-to-cart button reachable while
 * scrolling, by showing a fixed bottom bar once that button scrolls out of view. It forwards
 * clicks to the real button instead of duplicating cart logic, so availability, quantity and
 * variant state always stay correct.
 *
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLButtonElement} stickyButton
 * @property {HTMLElement} stickyButtonLabel
 * @extends {Component<StickyAddToCartRefs>}
 */
export class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyButton', 'stickyButtonLabel'];

  /** @type {HTMLButtonElement | null} */
  #sourceButton = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #sourceObserver = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const sourceButton = this.#findSourceButton();

    if (!sourceButton) {
      // Nothing to mirror (e.g. pre-order CTA, gift card form) - don't show an inert bar.
      this.remove();
      return;
    }

    this.#sourceButton = sourceButton;
    this.#syncFromSource();

    this.refs.stickyButton.addEventListener('click', this.#handleClick);

    this.#sourceObserver = new MutationObserver(() => this.#syncFromSource());
    this.#sourceObserver.observe(sourceButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    const target = sourceButton.closest('.product-form-buttons') ?? sourceButton;
    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.toggleAttribute('data-visible', entry.boundingClientRect.bottom < 0);
    });
    this.#intersectionObserver.observe(target);

    // Dialogs (cart drawer, search, menu) lock body scroll by fixing its position - use that as
    // a signal to hide the bar so it never visually competes with an open dialog.
    this.#dialogObserver = new MutationObserver(() => {
      this.toggleAttribute('data-dialog-open', document.body.style.position === 'fixed');
    });
    this.#dialogObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    this.#sourceObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.refs.stickyButton?.removeEventListener('click', this.#handleClick);
  }

  /**
   * Finds the add-to-cart button of the main product form for this product.
   * @returns {HTMLButtonElement | null}
   */
  #findSourceButton() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const form = document.querySelector(
      `#MainContent product-form-component[data-product-id="${CSS.escape(productId)}"] [data-type="add-to-cart-form"]`,
    );
    const button = form?.querySelector('button[name="add"]');

    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Forwards the click to the real add-to-cart button so the existing cart logic
   * (validation, variant, quantity) runs exactly as if the customer clicked it directly.
   */
  #handleClick = () => {
    this.#sourceButton?.click();
  };

  /**
   * Mirrors the real button's disabled state and label onto the sticky button.
   */
  #syncFromSource() {
    const source = this.#sourceButton;
    if (!source) return;

    this.refs.stickyButton.disabled = source.disabled;

    const labelSource = source.classList.contains('atc-added')
      ? source.querySelector('.add-to-cart-text--added')
      : source.querySelector('.add-to-cart-text__content');

    const label = labelSource?.textContent?.trim();
    if (label) this.refs.stickyButtonLabel.textContent = label;
  }
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
}
